import { useState } from 'react';
import { UserCog, LogOut } from 'lucide-react';
import { useAuth } from '@/lib/auth-store';
import { stopMasquerading } from '@/api/auth';

/**
 * MasqueradeBanner
 * ─────────────────────────────────────────────────────────────────────
 * Renders when a super_admin or reseller_owner is currently impersonating
 * another user. Sits ABOVE the CustomerContextBanner because the "I am
 * pretending to be this user" state is the highest-priority context
 * signal — you should never lose sight of it.
 *
 * The styling mirrors CustomerContextBanner's diagonal-stripe motif, but
 * uses a destructive (red) surface so it reads as "elevated, careful
 * action" rather than "routine scope indicator".
 */
export function MasqueradeBanner() {
  const impersonating = useAuth((s) => s.impersonating);
  const originalUser = useAuth((s) => s.originalUser);
  const [stopping, setStopping] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!impersonating) return null;

  async function handleStop() {
    setStopping(true);
    setError(null);
    try {
      await stopMasquerading();
      // Hard reload so every query, route guard, and nav item re-evaluates
      // under the restored original identity without stale cached responses.
      window.location.assign('/dashboard');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to stop masquerading.');
      setStopping(false);
    }
  }

  const roleLabel = impersonating.role.replace(/_/g, ' ');

  return (
    <div
      role="alert"
      aria-live="assertive"
      className="sticky top-0 z-30 bg-destructive text-destructive-foreground shadow-[0_2px_0_0_hsl(0_84%_40%)]"
    >
      <div
        aria-hidden
        className="absolute inset-0 opacity-[0.1] pointer-events-none"
        style={{
          backgroundImage:
            'repeating-linear-gradient(45deg, transparent 0 8px, rgba(255,255,255,0.7) 8px 9px)',
        }}
      />
      <div className="relative mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex flex-wrap items-center justify-between gap-3 py-2.5">
          <div className="flex items-center gap-3 min-w-0">
            <span
              aria-hidden
              className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-sm bg-destructive-foreground/15 ring-1 ring-destructive-foreground/25"
            >
              <UserCog className="h-3.5 w-3.5" />
            </span>
            <div className="min-w-0">
              <div className="text-[10px] font-mono uppercase tracking-[0.22em] opacity-80 leading-none">
                Masquerading
              </div>
              <div className="mt-1 text-sm font-semibold leading-tight truncate" dir="auto">
                {impersonating.email}
                <span className="ms-2 inline-flex items-center rounded-sm bg-destructive-foreground/15 px-1.5 py-0.5 text-[10px] font-mono font-semibold tracking-wider leading-none align-middle uppercase">
                  {roleLabel}
                </span>
                {originalUser && (
                  <span className="ms-2 text-[11px] font-normal opacity-80">
                    (as {originalUser.email})
                  </span>
                )}
              </div>
              {error && (
                <div className="mt-1 text-[11px] font-medium opacity-90" role="status">
                  {error}
                </div>
              )}
            </div>
          </div>

          <button
            type="button"
            onClick={handleStop}
            disabled={stopping}
            className="inline-flex flex-shrink-0 items-center gap-1.5 rounded-md border border-destructive-foreground/30 bg-destructive-foreground/10 px-3 py-1.5 text-xs font-medium tracking-wide transition-colors hover:bg-destructive-foreground/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-destructive-foreground/60 focus-visible:ring-offset-2 focus-visible:ring-offset-destructive disabled:opacity-60 disabled:cursor-not-allowed"
            aria-label={`Stop masquerading as ${impersonating.email}`}
          >
            <LogOut className="h-3.5 w-3.5" />
            <span>{stopping ? 'Stopping…' : 'Stop masquerading'}</span>
          </button>
        </div>
      </div>
    </div>
  );
}
